import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Spinner,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import { FiUsers } from "react-icons/fi";
import {
  EmailInviteStatusDto,
  OrganizationInviteStatusV2Dto,
  OrganizationType,
  SourcingEventStatus,
  useAddOrganizationInviteToPrivateSourcingEventMutation,
  useCreateEmailInviteReminderForPrivateSourcingEventMutation,
  useGetOrganizationQuery,
  useRemoveEmailInviteFromPrivateSourcingEventMutation,
  useRemoveOrganizationInviteFromPrivateSourcingEventMutation,
  type EmailInviteDto,
  type MinimalOrgDto,
  type OrganizationInviteV2Dto,
  type Private,
  type SourcingEventV2Dto,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { displayDateWithTime } from "../../../common/formatting/displayDateWithTime";
import { OrganizationSelector } from "../../../common/organization/OrganizationSelector";
import { OrganizationTypeTag } from "../../../common/organization/OrganizationTypeTag";
import { useSub } from "../../../common/subscription/useSub";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { CreateOrganizationEntryModal } from "../../organizations/entry/create-entry/CreateOrganizationEntryModal";
import { EmaiInviteStatusTag } from "../../sourcing-events/buyer/view-single/EmailInviteStatusTag";
import {
  V2InviteOrganizationModal,
  type OrganizationInviteRecipients,
} from "../../sourcing-events/buyer/wizard/steps/private/V2InviteOrganizationModal";
import { SectionCard } from "../../sourcing-events/portal/view-single/SectionCard";
import { OrgInviteStatusTag } from "./OrgInviteStatusTag";
import { V2InviteOrganizationEntryModal } from "./V2InviteOrganizationEntryModal";

interface Props {
  event: SourcingEventV2Dto & { eventContext: Private };
}

export const InvitesSection = ({ event }: Props) => {
  const { t } = useTranslation();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { trigger } = useApiError();
  const sub = useSub();
  const confirmationModal = useConfirmationModal();
  const toast = useToast();

  const [addOrgInviteMutation] = useAddOrganizationInviteToPrivateSourcingEventMutation();
  const [removeOrgInviteMutation, { isLoading: isRemovingOrgInvite }] =
    useRemoveOrganizationInviteFromPrivateSourcingEventMutation();
  const [removeEmailInvite, { isLoading: isRemovingEmailInvite }] =
    useRemoveEmailInviteFromPrivateSourcingEventMutation();
  const [sendReminder] = useCreateEmailInviteReminderForPrivateSourcingEventMutation();

  const [selectedOrgEntry, setSelectedOrgEntry] = useState<MinimalOrgDto | undefined>();
  const [selectedOrgForRecipients, setSelectedOrgForRecipients] = useState<MinimalOrgDto | undefined>();
  const [showCreateEntryModal, setShowCreateEntryModal] = useState(false);

  const orgInvites = event.eventContext.organizationInvites;
  const emailInvites: EmailInviteDto[] = event.eventContext.emailInvites ?? [];
  const isDraft = event.status === SourcingEventStatus.Draft;
  const isPublished = event.status === SourcingEventStatus.Published;
  const canManageInvites = sub.canCreateEvents;
  const canAddInvites = canManageInvites && (isDraft || isPublished);
  const showStatus = !isDraft;

  const invitedOrgIds = useMemo(() => orgInvites.map((invite) => invite.organizationId), [orgInvites]);
  const orgsToExclude = useMemo(
    () => [
      selectedOrg.id,
      ...invitedOrgIds,
      ...emailInvites.map((invite) => invite.organizationEntry?.id).filter((id): id is string => !!id),
    ],
    [selectedOrg.id, invitedOrgIds, emailInvites],
  );

  const addOrgInvite = async (organization: MinimalOrgDto, recipients: OrganizationInviteRecipients) => {
    if (invitedOrgIds.includes(organization.id)) return;
    try {
      await addOrgInviteMutation({
        eventId: event.id,
        addOrganizationInviteRequest: {
          organizationId: organization.id,
          persons: recipients.personIds.map((id) => ({ id })),
          emailInvites: recipients.emailInvites,
        },
      }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };

  const removeOrgInvite = async (organizationId: string) => {
    try {
      await removeOrgInviteMutation({ eventId: event.id, pathOrganizationId: organizationId }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };

  const confirmRemoveOrgInvite = (organizationId: string) => {
    if (isDraft) {
      removeOrgInvite(organizationId);
      return;
    }
    confirmationModal.show({
      title: t("Confirm removal"),
      description: t("The selected organization will immediately be removed from the event."),
      onConfirmed: () => removeOrgInvite(organizationId),
      confirmButtonColor: "red",
      confirmButtonText: t("Remove") ?? "",
    });
  };

  const selectOrganization = (organization?: MinimalOrgDto) => {
    if (!organization) return;
    if (organization.type === OrganizationType.Organization) {
      if (!invitedOrgIds.includes(organization.id)) setSelectedOrgForRecipients(organization);
    } else {
      setSelectedOrgEntry(organization);
    }
  };

  const confirmRemoveEmailInvite = (invite: EmailInviteDto) => {
    confirmationModal.show({
      title: t("Confirm removal"),
      description: t("The selected email invite will be removed."),
      onConfirmed: async () => {
        try {
          await removeEmailInvite({ eventId: event.id, inviteId: invite.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
      confirmButtonColor: "red",
      confirmButtonText: t("Remove") ?? "",
    });
  };

  const confirmSendReminder = (invite: EmailInviteDto) => {
    confirmationModal.show({
      title: t("Send email reminder?"),
      description: `${t("Are you sure you want to send an email reminder to")} ${invite.email}`,
      confirmButtonText: t("Send reminder") ?? "Send reminder",
      onConfirmed: async () => {
        try {
          await sendReminder({ eventId: event.id, inviteId: invite.id }).unwrap();
          toast({
            title: t("Email reminder sent!"),
            description: t("The email reminder was successfully sent"),
            status: "success",
          });
        } catch (e) {
          toast({
            title: t("Could not send reminder"),
            description: t("Something went wrong, please try again"),
            status: "error",
          });
        }
      },
    });
  };

  return (
    <SectionCard icon={FiUsers} title={t("Invited organizations")} count={orgInvites.length + emailInvites.length}>
      {confirmationModal.modal}

      {showCreateEntryModal && (
        <CreateOrganizationEntryModal
          onConfirm={async (newEntry) => {
            setSelectedOrgEntry({ type: OrganizationType.OrganizationEntry, ...newEntry });
          }}
          onSelectExisting={(org) => selectOrganization(org)}
          onClose={() => setShowCreateEntryModal(false)}
        />
      )}

      <V2InviteOrganizationEntryModal
        eventId={event.id}
        minimalOrgEntry={selectedOrgEntry}
        onClose={() => setSelectedOrgEntry(undefined)}
      />

      <V2InviteOrganizationModal
        minimalOrg={selectedOrgForRecipients}
        onClose={() => setSelectedOrgForRecipients(undefined)}
        onCreate={(recipients) => {
          if (selectedOrgForRecipients) addOrgInvite(selectedOrgForRecipients, recipients);
        }}
        showPublishedWarning={isPublished}
      />

      {canAddInvites && (
        <Box pb="5" data-testid="invite-organization-selector">
          <Text color="smMuted" fontSize="sm" pb="2">
            {t("Invite organizations to this private procurement.")}
          </Text>
          <Flex maxW={{ base: "full", md: "md" }}>
            <OrganizationSelector
              orgsToExclude={orgsToExclude}
              onChange={({ organization }) => selectOrganization(organization)}
            />
          </Flex>
          <Text fontSize="sm" color="smMuted" pt="1">
            {t("Choose a registered company, or")}{" "}
            <Button
              variant="link"
              colorScheme="blue"
              fontWeight="bold"
              fontSize="sm"
              onClick={() => setShowCreateEntryModal(true)}
            >
              {t("create a new company entry")}
            </Button>
          </Text>
        </Box>
      )}

      <Box border="1px solid" borderColor="smBorder" rounded="md" overflow="hidden">
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>{t("Organization name")}</Th>
                <Th>{t("Type")}</Th>
                <Th>{t("Recipients")}</Th>
                {showStatus && <Th>{t("Status")}</Th>}
                {canAddInvites && <Th textAlign="right">{t("Remove")}</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {orgInvites.map((invite) => (
                <InvitedOrgRow
                  key={invite.organizationId}
                  invite={invite}
                  showStatus={showStatus}
                  showActions={canAddInvites}
                  showRemove={canAddInvites}
                  isRemoving={isRemovingOrgInvite}
                  onRemove={() => confirmRemoveOrgInvite(invite.organizationId)}
                />
              ))}
              {emailInvites.map((invite) => (
                <Tr key={invite.id}>
                  <Td verticalAlign="top">
                    <Text fontSize="sm">{invite.organizationEntry?.name ?? t("No organization")}</Text>
                  </Td>
                  <Td verticalAlign="top">
                    <OrganizationTypeTag type={OrganizationType.OrganizationEntry} />
                  </Td>
                  <Td verticalAlign="top">
                    <Text fontSize="sm">{invite.email}</Text>
                  </Td>
                  {showStatus && (
                    <Td verticalAlign="top">
                      <Flex direction="column" align="flex-start" gap="1">
                        <EmaiInviteStatusTag
                          status={invite.status}
                          openedAt={invite.openedAt ?? undefined}
                          size="sm"
                        />
                        {invite.lastReminderSentAt && (
                          <Text fontSize="xs" fontStyle="italic">
                            {t("Last reminder sent")} {displayDateWithTime(invite.lastReminderSentAt, t("at"))}
                          </Text>
                        )}
                      </Flex>
                    </Td>
                  )}
                  {canAddInvites && (
                    <Td verticalAlign="top" textAlign="right">
                      <Flex justify="flex-end" align="center" gap="2">
                        {isPublished && invite.status !== EmailInviteStatusDto.SendingFailed && (
                          <Button
                            variant="solid"
                            size="xs"
                            colorScheme="orange"
                            onClick={() => confirmSendReminder(invite)}
                          >
                            {t("Send reminder")}
                          </Button>
                        )}
                        <IconButton
                          aria-label={t("Remove") ?? "Remove"}
                          icon={<Icon as={FaTimes} />}
                          size="sm"
                          variant="ghost"
                          isDisabled={isRemovingEmailInvite}
                          onClick={() => confirmRemoveEmailInvite(invite)}
                        />
                      </Flex>
                    </Td>
                  )}
                </Tr>
              ))}
              {orgInvites.length + emailInvites.length === 0 && (
                <Tr>
                  <Td
                    colSpan={3 + (showStatus ? 1 : 0) + (canAddInvites ? 1 : 0)}
                    textAlign="center"
                    color="smMuted"
                  >
                    {t("No invites have been added")}
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </SectionCard>
  );
};

const InvitedOrgRow = ({
  invite,
  showStatus,
  showActions,
  showRemove,
  isRemoving,
  onRemove,
}: {
  invite: OrganizationInviteV2Dto;
  showStatus: boolean;
  showActions: boolean;
  showRemove: boolean;
  isRemoving: boolean;
  onRemove: () => void;
}) => {
  const { t } = useTranslation();
  const { data: org, isLoading } = useGetOrganizationQuery({ orgId: invite.organizationId });

  return (
    <Tr>
      <Td verticalAlign="top">
        {isLoading ? (
          <Spinner size="xs" color="smPrimary" />
        ) : (
          <>
            <Text fontSize="sm">{org?.name ?? "–"}</Text>
            <Text fontSize="xs" color="smMuted">
              {org?.organizationNumber ?? "–"}
            </Text>
          </>
        )}
      </Td>
      <Td verticalAlign="top">
        <OrganizationTypeTag type={OrganizationType.Organization} />
      </Td>
      <Td verticalAlign="top">
        {(invite.persons ?? []).map((person) => (
          <Text key={person.id} fontSize="sm">
            {`${person.firstName} ${person.lastName} (${person.email})`.trim()}
          </Text>
        ))}
        {(invite.emailInvites ?? []).map((email) => (
          <Text key={email.email} fontSize="sm" pt="1">
            {email.email} <Tag size="sm">{t("Not registered")}</Tag>
          </Text>
        ))}
        {!invite.persons?.length && !invite.emailInvites?.length && (
          <Text fontSize="sm" color="smMuted">
            –
          </Text>
        )}
      </Td>
      {showStatus && (
        <Td verticalAlign="top">
          <OrgInviteStatusTag status={invite.status ?? OrganizationInviteStatusV2Dto.Invited} />
        </Td>
      )}
      {showActions && (
        <Td verticalAlign="top" textAlign="right">
          {showRemove && (
            <IconButton
              aria-label={t("Remove") ?? "Remove"}
              icon={<Icon as={FaTimes} />}
              size="sm"
              variant="ghost"
              isDisabled={isRemoving}
              onClick={onRemove}
            />
          )}
        </Td>
      )}
    </Tr>
  );
};
